import React, { Component, useMemo, useCallback } from "react";
import {
  Card,
  Typography,
  CardContent,
  Grid,
  withStyles,
  Paper,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { connect } from "react-redux";
import { appService } from "../../App/app.service";
import { ResizableTable } from "../../components/resizableTable/ResizableTable";
import AddHeaderDescriptionWithHelperText from "../../components/HelperTextDescription";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { countOrACVFormatter, percentageFormatter } from "../../util/customFunctions";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "7rem",
    fontSize: "0.9rem",
    color: "black",
    textAlign: "center",
    background: "white",
    border: "0.5px solid lightgrey",
    cursor: "default",
  },
});

const styles = (theme) => ({
  card: {
    marginBottom: theme.spacing(4), // Adds space between skeleton cards
  },
});

// Helper function to calculate default column width based on text length (matching DynamicTable logic)
const calculateColumnWidth = (textLength) => {
  if (textLength >= 0 && textLength <= 2) {
    return 48; // 0.6rem * 16px/rem ≈ 9.6px, but using 48px for better visibility
  } else if (textLength >= 3 && textLength <= 5) {
    return 60; // 0.93rem * 16px/rem ≈ 14.88px, using 60px
  } else if (textLength >= 6 && textLength <= 10) {
    return 80; // 1.25rem * 16px/rem = 20px, using 80px
  } else if (textLength >= 11 && textLength <= 20) {
    return 120; // 1.87rem * 16px/rem ≈ 29.92px, using 120px
  } else if (textLength >= 21 && textLength <= 30) {
    return 180; // 2.81rem * 16px/rem ≈ 44.96px, using 180px
  } else if (textLength >= 31 && textLength <= 45) {
    return 240; // 3rem * 16px/rem = 48px, using 240px
  } else if (textLength >= 46 && textLength <= 60) {
    return 352; // 22rem * 16px/rem = 352px
  } else if (textLength > 60) {
    return 400; // 25rem * 16px/rem = 400px
  }
  return 120; // default
};

// Transform config and tableData to ResizableTable format
const transformToResizableTableFormat = (config, tableData, currencySymbol) => {
  // Filter out Highlight_Flag column
  const filteredConfig = config.filter((col) => col.UI_column !== "Highlight_Flag");

  // Calculate default widths based on sample data
  const calculateDefaultWidth = (col) => {
    if (col.UI_column === "Rank") {
      return 48; // 3rem = 48px
    }
    
    // Find max text length in this column
    let maxLength = col.UI_column.length; // Start with header length
    if (tableData && tableData.length > 0) {
      tableData.slice(0, 10).forEach((row) => {
        const value = row[col.UI_column];
        let formattedValue = "";
        
        if (col.data_type === "acv") {
          formattedValue = value != null ? countOrACVFormatter(value, true) : "";
        } else if (col.data_type === "percentage") {
          formattedValue = value != null ? percentageFormatter(value) : "";
        } else if (col.data_type === "boolean") {
          formattedValue = value != null ? (value ? "Y" : "") : "";
        } else if (col.data_type === "date") {
          if (value) {
            const dateObj = new Date(value);
            formattedValue = !isNaN(dateObj.getTime())
              ? `${dateObj.getMonth() + 1}/${dateObj.getDate()}/${dateObj.getFullYear()}`
              : "";
          }
        } else {
          formattedValue = value ?? "";
        }
        
        const stringValue = String(formattedValue || "").trim();
        maxLength = Math.max(maxLength, stringValue.length);
      });
    }
    
    return calculateColumnWidth(maxLength);
  };

  // Create columns array for ResizableTable
  const columns = [
    // First column: "#" (Rank/Index)
    {
      label: "#",
      sortable: false, // Disable sorting on "#" column
      subColumns: [
        {
          label: "#",
          field: "_index",
          type: "number",
          align: "center",
          minWidth: 48,
          width: 48,
          sortable: false, // Disable sorting on "#" sub-column
        },
      ],
      headerBgColor: "#4471c4",
      headerTextColor: "#ffffff",
    },
    // Other columns from config
    ...filteredConfig.map((col) => {
      let columnType = "text";
      if (col.data_type === "acv") {
        columnType = "currency";
      } else if (col.data_type === "percentage") {
        columnType = "percentage";
      } else if (col.data_type === "number" || col.data_type === "count") {
        columnType = "number";
      } else if (col.data_type === "boolean") {
        columnType = "text";
      } else if (col.data_type === "date") {
        columnType = "date";
      }

      const defaultWidth = calculateDefaultWidth(col);

      return {
        label: col.UI_column,
        sortable: true, // Enable sorting on main header
        subColumns: [
          {
            label: col.UI_column,
            field: col.UI_column,
            type: columnType,
            dataType: col.data_type, // Preserve original data_type for filtering
            align: col.align || "left",
            minWidth: col.UI_column === "Rank" ? 48 : 50,
            width: defaultWidth,
            tooltip: col.tooltip_text,
            sortable: true, // Enable sorting on sub-column
            filterable: true, // Enable filtering on sub-column
          },
        ],
        headerBgColor: "#4471c4",
        headerTextColor: "#ffffff",
      };
    }),
  ];

  // Transform tableData to rows format (add index)
  const rows = tableData?.map((row, idx) => ({
    ...row,
    _index: idx + 1,
  })) || [];

  return { columns, rows };
};

// Component to render the table with ResizableTable
const FocusRecommendationTable = ({
  config,
  tableData,
  tableTitle,
  description,
  displayMessage,
  tab,
  currencySymbol,
  loading,
}) => {
  const { columns, rows } = useMemo(() => {
    if (!config || !tableData || tableData.length === 0) {
      return { columns: [], rows: [] };
    }
    return transformToResizableTableFormat(config, tableData, currencySymbol);
  }, [config, tableData, currencySymbol]);

  // Format cell value based on column config
  const getCellValue = useCallback((row, field, rowIndex) => {
    if (field === "_index") {
      // Return current display position (1-based) - always shows 1, 2, 3, 4, 5... in order
      // rowIndex is 0-based, so add 1 to make it 1-based
      return rowIndex !== undefined ? rowIndex + 1 : (row._index || 1);
    }

    const col = config?.find((c) => c.UI_column === field);
    if (!col) return row[field] ?? "";

    const value = row[field];

    // Format based on data type
    if (col.data_type === "acv") {
      return value != null ? countOrACVFormatter(value, true) : "";
    } else if (col.data_type === "percentage") {
      return value != null ? percentageFormatter(value) : "";
    } else if (col.data_type === "boolean") {
      return value != null ? (value ? "Y" : "") : "";
    } else if (col.data_type === "date") {
      if (value) {
        const dateObj = new Date(value);
        return !isNaN(dateObj.getTime())
          ? `${dateObj.getMonth() + 1}/${dateObj.getDate()}/${dateObj.getFullYear()}`
          : "";
      }
      return "";
    }

    return value ?? "";
  }, [config]);

  // Get cell style for highlighting
  const getCellStyle = useCallback((row, field) => {
    const style = {};
    
    // Highlight anomaly rows (Highlight_Flag = "Y")
    const isHighlighted =
      row.Highlight_Flag && String(row.Highlight_Flag).toLowerCase() === "y";
    if (isHighlighted) {
      style.backgroundColor = "rgba(244, 177, 131, 0.5)";
    }

    // Highlight Top 5 Rank cells
    const isTop5 = row.Rank && row.Rank >= 1 && row.Rank <= 5;
    if (isTop5 && field === "Rank") {
      style.backgroundColor = "#c6e0b4";
    }

    // Gray out cells when Rank is null/undefined
    if (!row.Rank && field !== "_index") {
      style.color = "gray";
    }

    return style;
  }, []);

  // Get row tooltip for highlighted rows
  const getRowTooltip = useCallback((row) => {
    const isHighlighted =
      row.Highlight_Flag && String(row.Highlight_Flag).toLowerCase() === "y";
    if (isHighlighted) {
      return {
        title: "Opportunities with low momentum but high rep confidence",
        placement: "top",
        followCursor: true,
        TooltipComponent: CustomWidthTooltip,
      };
    }
    return null;
  }, []);

  if (loading && (!tableData || tableData.length === 0)) {
    return (
      <Card raised style={{ margin: "auto", width: "100%", marginTop: 24 }}>
        <CardContent
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Skeleton variant="rectangular" width="90%" height={240} />
        </CardContent>
      </Card>
    );
  }

  if (!tableData || tableData.length === 0) {
    return (
      <Card raised style={{ margin: "auto", width: "100%", marginTop: 24 }}>
        <CardContent
          style={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography>{displayMessage}</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <Card raised style={{ margin: "auto", width: "100%", marginTop: 24 }}>
        <Typography
          align="center"
          variant="h2"
          style={{ margin: "1rem", fontWeight: 600 }}
        >
          {tableTitle}
        </Typography>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <Typography
            variant="body2"
            style={{
              fontSize: "0.85rem",
              fontWeight: 400,
              whiteSpace: "pre-line",
              textAlign: "center",
              maxWidth: "80%",
            }}
          >
            {description}
          </Typography>
        </div>

        <CardContent style={{ padding: "1rem" }}>
          <ResizableTable
            columns={columns}
            rows={rows}
            currencySymbol={currencySymbol}
            getCellValue={getCellValue}
            getCellStyle={getCellStyle}
            getRowTooltip={getRowTooltip}
            rowKey="_index"
            heading={null}
            maxWidth="100%"
            hideSubHeaderRow={true}
          />

          {/* Legend - Below the table */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
            <div style={{ display: "flex", gap: "2rem", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <div
                  style={{
                    width: "1.5rem",
                    height: "1.5rem",
                    backgroundColor: "#c6e0b4",
                  }}
                ></div>
                <Typography variant="body2" style={{ fontSize: "0.85rem" }}>
                  Top 5 Recommended Opps
                </Typography>
              </div>
              {tab === "Nudge" && (
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <div
                    style={{
                      width: "1.5rem",
                      height: "1.5rem",
                      backgroundColor: "rgba(244, 177, 131, 0.5)",
                    }}
                  ></div>
                  <Typography variant="body2" style={{ fontSize: "0.85rem" }}>
                    Anomaly Opps
                  </Typography>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

class FocusRecommendation extends Component {
  state = {
    loading: true,
    error: null,
    tab: "Nudge",
  };

  componentDidMount() {
    this.getData();
  }

  getData = async () => {
    this.setState({ loading: true, error: null });
    try {
      await this.props.getFocusRecommendationData();
    } catch (error) {
      console.error("Error loading focus recommendations:", error);
      this.setState({
        error: "Failed to load focus recommendations data. Please try again.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleChangeTab = (e) => {
    // this.handleGo();
    console.log(e.target.value);
    this.setState({
      ...this.state,
      tab: e.target.value,
    });
  };

  renderSkeletonCard = (key) => {
    const { classes } = this.props;
    return (
      <Card className={classes.card} raised key={key}>
        <CardContent
          style={{
            height: "90%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Skeleton variant="rectangular" width="90%" height={200} />
        </CardContent>
      </Card>
    );
  };

  render() {
    const { loading, error } = this.state;
    const { FocusRecommendationData, classes } = this.props;


    return (
      <Grid container direction="column" xs={12}>
        <AddHeaderDescriptionWithHelperText
          header={`Opportunities for Increased Focus`}
          description={`Current and Next Quarter Opportunities for increased focus`}
        />
                <Grid
                    item
                    xs={12}
                    md={12}
                    id="grid-container"
                    style={{ marginTop: 10,marginBottom: '1rem' }}
                  >
                    <Paper>
                      <ToggleButtonGroup
                        color="primary"
                        indicatorColor="primary"
                        value={this.state.tab}
                        exclusive
                        size="large"
                        onChange={this.handleChangeTab}
                      >
                        <ToggleButton
                          style={{
                            background: this.state.tab === "Nudge" ? "#4472C4" : "",
                            color: this.state.tab === "Nudge" ? "white" : "",
                            fontSize: "1rem",
                            fontWeight: 600,
                            height: "3.5rem",
                            width: "18rem",
                          }}
                          value="Nudge"
                        >
                          Nudge (Current Qtr. Opps)
                        </ToggleButton>
                        <ToggleButton
                          style={{
                            background: this.state.tab === "Pulled-In" ? "#4472C4" : "",
                            color: this.state.tab === "Pulled-In" ? "white" : "",
                            fontSize: "1rem",
                            fontWeight: 600,
                            height: "3.5rem",
                            width: "18rem",
                          }}
                          value="Pulled-In"
                        >
                          Pull In (Next Qtr. Opps)
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Paper>
                  </Grid>
        {loading ? (
          <>
            {this.renderSkeletonCard(1)}
            {this.renderSkeletonCard(2)}
          </>
        ) : error ? (
          <Typography color="error" align="center">
            {error}
          </Typography>
        ) : (
          <>
          {this.state.tab === "Nudge" && (
            <Grid container xs={12}>
              <FocusRecommendationTable
                tableTitle={`${FocusRecommendationData.currentFiscalQuarter} opportunities recommended for increased focus`}
                description={`The following ${FocusRecommendationData.currentFiscalQuarter} opportunities with a Rank have a high likelihood of being won if given a nudge to the next stage.
                Opportunities without a Rank are not recommended for a nudge. 
                Opportunities with low momentum with high rep confidence are highlighted with a light orange background and deserve deeper inspection.
                `}
                tableData={FocusRecommendationData.focusTableData}
                config={FocusRecommendationData.nudgeTableConfig}
                loading={loading}
                displayMessage={'No Data Available'}
                tab={this.state.tab}
                currencySymbol={this.props.user?.Currency || "$"}
              />
            </Grid>
            )}
            {this.state.tab === "Pulled-In" && (
            <Grid container xs={12}>
              <FocusRecommendationTable
                tableTitle={`${FocusRecommendationData.nextFiscalQuarter} opportunities recommended for being pulled in`}
                tableData={FocusRecommendationData.pullInTableData}
                config={FocusRecommendationData.pullInTableConfig}
                loading={loading}
                description={`The following ${FocusRecommendationData.nextFiscalQuarter} opportunities with a Rank have projected close dates early in the quarter and have a high likelihood of being pulled into the current quarter and won.
                              Opportunities without a Rank are not recommended for being pulled in.
                              SkyGeni typically recommends pull in opportunities just after the midpoint of the quarter.
                              `}
                displayMessage={`Pull in recommendations for ${FocusRecommendationData.nextFiscalQuarter} opportunities are not yet available.`}
                tab={this.state.tab}
                currencySymbol={this.props.user?.Currency || "$"}
              />
            </Grid>
            )}
          </>
        )}
      </Grid>
    );
  }
}

/* Redux */
const mapStateToProps = (state) => {
  const { user, FocusRecommendationData } = state.app;
  return {
    company: user?.company,
    user,
    FocusRecommendationData,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getFocusRecommendationData: () => {
    return appService.getFocusRecommendationData().then(
      (json) => {
        dispatch({ type: "get_Focus_Recommendation", json });
        return json;
      },
      (error) => {
        console.error("API error:", error);
        throw error;
      }
    );
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(FocusRecommendation));
